import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import {
  fetchCampaignPlans,
  fetchCampaignPlan,
  createCampaignPlan,
  updateCampaignPlan,
  deleteCampaignPlan,
  fetchCampaignPlanAssets,
  upsertCampaignPlanAssets,
  updateCampaignPlanAsset,
  CampaignPlan,
  CampaignPlanAsset,
} from '@/services/campaignPlannerService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useCampaignPlans = () => {
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  return useQuery({
    queryKey: ['campaign-plans', companyId],
    queryFn: () => fetchCampaignPlans(companyId!),
    enabled: !!companyId,
  });
};

export const useCampaignPlan = (id: string | undefined) => {
  return useQuery({
    queryKey: ['campaign-plan', id],
    queryFn: () => fetchCampaignPlan(id!),
    enabled: !!id,
  });
};

export const useCampaignPlanAssets = (planId: string | undefined) => {
  return useQuery({
    queryKey: ['campaign-plan-assets', planId],
    queryFn: () => fetchCampaignPlanAssets(planId!),
    enabled: !!planId,
  });
};

export const useCreateCampaignPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCampaignPlan,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-plans'] });
    },
  });
};

export const useUpdateCampaignPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CampaignPlan> }) =>
      updateCampaignPlan(id, updates),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['campaign-plans'] });
      qc.invalidateQueries({ queryKey: ['campaign-plan', id] });
    },
  });
};

export const useDeleteCampaignPlan = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteCampaignPlan,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-plans'] });
      toast.success('Campaign plan deleted');
    },
  });
};

export const useUpdateCampaignPlanAsset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CampaignPlanAsset> }) =>
      updateCampaignPlanAsset(id, updates),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaign-plan-assets', data.campaign_plan_id] });
    },
  });
};

export const useGenerateStrategy = () => {
  return useMutation({
    mutationFn: async (payload: { idea: string; goal: string; companyId: string }) => {
      const { data, error } = await supabase.functions.invoke('generate-campaign-strategy', {
        body: payload,
      });
      if (error) throw error;
      return data;
    },
  });
};

export const useGenerateAssets = () => {
  return useMutation({
    mutationFn: async (payload: { planId: string; strategicBrief: any; tone: string; companyId: string }) => {
      const { data, error } = await supabase.functions.invoke('generate-campaign-assets', {
        body: payload,
      });
      if (error) throw error;
      return data;
    },
  });
};
